import React, { useContext, useEffect, useRef, useState } from "react";
import {
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
  IonSegment,
  IonSegmentButton,
  IonToolbar,
  useIonRouter,
  isPlatform,
  IonText,
} from "@ionic/react";
import {
  bagHandleOutline,
  bagHandleSharp,
  buildOutline,
  buildSharp,
  homeOutline,
  homeSharp,
  cogOutline,
  cogSharp,
  createOutline,
  createSharp,
  hammerOutline,
  hammerSharp,
  construct,
  cart,
  settings,
  logOut,
  qrCodeOutline,
  qrCodeSharp,
  repeatOutline,
  repeatSharp,
  swapHorizontalOutline,
  swapHorizontalSharp,
  cubeOutline,
  cubeSharp,
  thumbsUp,
  readerOutline,
  readerSharp,
  timeOutline,
  timeSharp,
  calendarOutline,
  calendarSharp,
  receiptOutline,
  receiptSharp,
  syncOutline,
  syncSharp,
  cloudOfflineOutline,
} from "ionicons/icons";
import "./Menu.css";

import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import {
  getApprovalAccess,
  getBinAccess,
  getCycleCountAccess,
  getItemAdjustAccess,
  getStoresAccess,
  getUserRequesterAccess,
  getUserSettings,
  getUserTechnicianAccess,
  loadUserSettings,
  getMobReqApprovalAccess,
  getPOReceiptsAccess,
  getScheduleAccess,
  getInspectionAccess,
} from "../util/UserHelper";
import { AuthContext, getAuthConfig } from "../util/OAuth";
import { useLocation } from "react-router";
import { BarcodeScanner } from "@capacitor-community/barcode-scanner";
import { encodeParam } from "../util/ApiHelper";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser";
import { useOfflineStore } from "../store/offline-mode";

interface AppPage {
  url?: string;
  iosIcon: string;
  mdIcon: string;
  title: string;
  children?: AppPage[];
  unavailableInOffline?: boolean;
}

const Menu: React.FC = () => {
  const technician = "Technician";
  const stores = "Stores";
  const mobileApproval = "Mobile Approval";
  const settingsTab = "Settings";
  const signoff = "SignOff";
  const endSession = "EndSession";
  const { translations } = useContext(TranslationsContext);
  const { loggedIn } = useContext(AuthContext);
  const location = useLocation();
  const [displayedAppPages, setDisplayedAppPages] = useState<AppPage[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [mainAccess, setMainAccess] = useState(false);
  const [storesAccess, setStoresAccess] = useState(false);
  const [approvalAccess, setApprovalAccess] = useState(false);
  const [tabLocation, setTabLocation] = useState<string>(technician);
  const [isTimeEntryModalOpen, setIsTimeEntryModalOpen] = useState(false);
  const [cloudSuiteManaged, setCloudSuiteManaged] = useState(false);
  const [mobReqApprovalAccess, setMobReqApprovalAccess] = useState(false);
  const [hasInspectionAccess, setHasInspectionAccess] = useState(false);
  const router = useIonRouter();
  const menuRef = useRef<HTMLIonMenuElement>(null);
  const isPWA = !isPlatform("hybrid");
  const [b2CSingleSignOutEnabled, setb2CSingleSignOutEnabled] = useState(false);
  const INSPECTION_ACCESS_KEY = "inspectionAccess";
  const isOffline = useOfflineStore((state) => state.isOffline);
  const { translatedMessages } = useContext(TranslationMessagesContext);

  const fetchUserSettings = async () => {
    await loadUserSettings();
    const settings = await getUserSettings();
    if (settings && settings.SessionSettingList) {
      setCloudSuiteManaged(settings.SessionSettingList.CloudSuiteManaged);
    }
    let authConfig = await getAuthConfig();
    const isB2CSingleSignOutEnabled = authConfig?.B2CSingleSignOutEnabled;
    setb2CSingleSignOutEnabled(isB2CSingleSignOutEnabled); // Ensure it's a boolean
  };

  useEffect(() => {
    const fetchAll = async () => {
      await fetchUserSettings();
    };
    fetchAll();
  }, []);

  // Base technician app pages without inspection
  const getBaseTechnicianPages = (): AppPage[] => {
    const pages: AppPage[] = [
      {
        title: isOffline
          ? translations["lbl_mob_my_downloads"] || "My Downloads"
          : translations["lbl_home"] || "Home",
        url: "/home",
        iosIcon: homeOutline,
        mdIcon: homeSharp,
      },
      {
        title: translations["lbl_WorkOrders"] || "Work Orders",
        url: "/WorkOrders",
        iosIcon: bagHandleOutline,
        mdIcon: bagHandleSharp,
      },
    ];

    pages.push({
      title: translations["lbl_category_inspection"] || "Inspection",
      url: "/MyInspections",
      iosIcon: hammerOutline,
      mdIcon: hammerSharp,
      unavailableInOffline: true,
    });

    pages.push(
      {
        title: translations["lbl_btn_equipment"] || "Equipment",
        url: "/Equipment",
        iosIcon: cogOutline,
        mdIcon: cogSharp,
      },
      {
        title: translations["lbl_btn_inventory"] || "Inventory",
        url: "/Inventory",
        iosIcon: cubeOutline,
        mdIcon: cubeSharp,
        unavailableInOffline: true,
      },
      {
        title: translations["lbl_mob_scanqr"] || "Scan QR Code",
        iosIcon: qrCodeOutline,
        mdIcon: qrCodeSharp,
        unavailableInOffline: true,
      },
      {
        title: translations["lbl_time_entry"] || "Time Entry",
        url: "/TimeEntryMenu",
        iosIcon: timeOutline,
        mdIcon: timeSharp,
        unavailableInOffline: true,
      },
      {
        title: translations["lbl_mob_myschedule"] || "My Schedule",
        url: "/MySchedule",
        iosIcon: calendarOutline,
        mdIcon: calendarSharp,
        unavailableInOffline: true,
      },
      {
        title:
          translations["lbl_mob_offline_upload_data"] || "Offline Upload Data",
        url: "/offline/upload-data",
        iosIcon: syncOutline,
        mdIcon: syncSharp,
      }
    );

    return pages;
  };

  const requesterAppPages: AppPage[] = [
    {
      title: translations["lbl_home"] || "Home",
      url: "/home",
      iosIcon: homeOutline,
      mdIcon: homeSharp,
    },
    {
      title: translations["lbl_create_request"] || "Create Request",
      url: "/requester/create-request/equipment",
      iosIcon: createOutline,
      mdIcon: createSharp,
    },
    {
      title: translations["lbl_Work_Order_Search"] || "Work Order Search",
      url: "/requester/work-orders",
      iosIcon: bagHandleOutline,
      mdIcon: bagHandleSharp,
    },
    {
      title: translations["lbl_mob_scanqr"] || "Scan QR Code",
      iosIcon: qrCodeOutline,
      mdIcon: qrCodeSharp,
    },
    {
      title: translations["lbl_time_entry"] || "Time Entry",
      url: "/TimeEntryMenu",
      iosIcon: timeOutline,
      mdIcon: timeSharp,
    },
    {
      title: translations["lbl_mob_myschedule"] || "My Schedule",
      url: "/MySchedule",
      iosIcon: calendarOutline,
      mdIcon: calendarSharp,
    },
  ];

  const storeAppPages: AppPage[] = [
    {
      title: translations["lbl_mob_issuereturn"] || "Issue/Returns",
      url: "/Stores",
      iosIcon: homeOutline,
      mdIcon: homeSharp,
    },
    {
      title: translations["lbl_btn_inventory"] || "Inventory",
      url: "/Inventory",
      iosIcon: cubeOutline,
      mdIcon: cubeSharp,
    },
  ];

  const binTransferTab = {
    title: translations["lbl_mob_bintransfer"] || "Bin Transfer",
    url: "/BinTransfer",
    iosIcon: swapHorizontalOutline,
    mdIcon: swapHorizontalSharp,
  };

  const cycleCountTab = {
    title: translations["lbl_category_cyclecount"] || "Cycle Count",
    url: "/CycleCount",
    iosIcon: repeatOutline,
    mdIcon: repeatSharp,
  };

  const itemAdjTab = {
    title: translations["lbl_mob_itemadjustment"] || "Item Adjustment",
    url: "/ItemAdjustment",
    iosIcon: buildOutline,
    mdIcon: buildSharp,
  };

  const poReceiptsTab = {
    title: translations["lbl_mob_po_receipts"] || "PO Receipts",
    url: "/POReceipts",
    iosIcon: receiptOutline,
    mdIcon: receiptSharp,
  };

  const mobileApprovalAppPages: AppPage[] = [
    {
      title: translations["lbl_WorkOrders"] || "Work Orders",
      url: "/WOApproval",
      iosIcon: bagHandleOutline,
      mdIcon: bagHandleSharp,
    },
    {
      title: translations["lbl_btn_requisition"] || "Requisition",
      url: "/RequisitionApproval",
      iosIcon: readerOutline,
      mdIcon: readerSharp,
    },
  ];

  const mobileReqApprovalAppPages: AppPage[] = [
    {
      title: translations["lbl_btn_requisition"] || "Requisition",
      url: "/RequisitionApproval",
      iosIcon: readerOutline,
      mdIcon: readerSharp,
    },
  ];

  const getAccessiblePages = async () => {
    if ((await getUserTechnicianAccess()) && tabLocation === technician) {
      let technicianMenuPages = getBaseTechnicianPages();
      if (!(await getScheduleAccess())) {
        technicianMenuPages = technicianMenuPages.filter((p) => p.url !== "/MySchedule");
      }
      if (!(await getInspectionAccess())) {
        technicianMenuPages = technicianMenuPages.filter((p) => p.url !== "/MyInspections");
      }
      setDisplayedAppPages([...technicianMenuPages]);
    } else if ((await getUserRequesterAccess()) && tabLocation === technician) {
      let reqPages = requesterAppPages;
      if (!(await getScheduleAccess())) {
        reqPages = reqPages.filter((p) => p.url !== "/MySchedule");
      }
      setDisplayedAppPages([...reqPages]);
    } else if ((await getStoresAccess()) && tabLocation === stores) {
      let storesAccessTab: AppPage[] = storeAppPages;
      if (await getBinAccess()) {
        storesAccessTab.push(binTransferTab);
      }
      if (await getCycleCountAccess()) {
        storesAccessTab.push(cycleCountTab);
      }
      if (await getItemAdjustAccess()) {
        storesAccessTab.push(itemAdjTab);
      }
      if (await getPOReceiptsAccess()) {
        storesAccessTab.push(poReceiptsTab);
      }
      setDisplayedAppPages([...storesAccessTab]);
    } else if ((await getApprovalAccess()) && tabLocation === mobileApproval) {
      setDisplayedAppPages([...mobileApprovalAppPages]);
    } else if (
      (await getMobReqApprovalAccess()) &&
      tabLocation === mobileApproval
    ) {
      setDisplayedAppPages([...mobileReqApprovalAppPages]);
    }
  };

  const handleSegmentChange = async (userAccess: string) => {
    if (userAccess === technician) {
      setTabLocation(technician);
    }
    if (userAccess === stores) {
      setTabLocation(stores);
    }
    if (userAccess === mobileApproval) {
      setTabLocation(mobileApproval);
    }
    if (userAccess === settingsTab) {
      menuRef.current && menuRef.current.close();
      router.push("/Settings");
    }
    if (userAccess === signoff) {
      router.push("/logout");
    }
    if (userAccess === endSession) {
      router.push("/endsession");
    }
  };

  const setupAccessLevel = async () => {
    let techReq = false,
      store = false,
      approval = false,
      mobReqApproval = false;
    if ((await getUserTechnicianAccess()) || (await getUserRequesterAccess())) {
      techReq = true;
    }
    if (await getStoresAccess()) {
      store = true;
    }
    if (await getApprovalAccess()) {
      approval = true;
    }
    if ((await getMobReqApprovalAccess()) && !techReq && !store && !approval) {
      mobReqApproval = true;
    }
    const inspectionAccess = await getInspectionAccess();
    setHasInspectionAccess(inspectionAccess);
    if (!techReq) {
      store && setTabLocation(stores);
      approval && setTabLocation(mobileApproval);
      mobReqApproval && setTabLocation(mobileApproval);
    }
    setMainAccess(techReq);
    setStoresAccess(store);
    setApprovalAccess(approval);
    setMobReqApprovalAccess(mobReqApproval);
    getAccessiblePages();
  };

  const onBarcodeScan = async (text?: string) => {
    if (!text) return;
    setSearchValue(text);

    const urlParams = new URLSearchParams(text);
    const equipmentId = urlParams.get("Equipment");
    if (!equipmentId) {
      //if the url does not have equipmentId in the url params then most likely it is a short url
      //try and get the final url using short URL (e.g., https://scanned.page/p/PEJnk5)
      const browser = InAppBrowser.create(text, "_self", { hidden: "no" });

      browser.on("loadstop").subscribe((event) => {
        const finalUrl = event.url;
        console.log("Final URL:", finalUrl);

        // If the final URL belongs to the app, route to it internally
        if (finalUrl.startsWith(window.location.origin)) {
          browser.close();
          const path = finalUrl.replace(window.location.origin, "");
          router.push(path);
        } else {
          router.push("/Equipment"); // go to equipment search page if nothing matches
        }
      });
    } else {
      const redirectUrl = `/Equipment/${encodeParam(equipmentId || "")}`;
      router.push(redirectUrl);
    }
  };

  const startScan = async () => {
    await BarcodeScanner.checkPermission({ force: true });
    await BarcodeScanner.prepare();
    document.body.classList.add("qrscanner");
    const result = await BarcodeScanner.startScan();

    if (result.hasContent) {
      onBarcodeScan(result.content);
    }
    stopScan();
  };

  const stopScan = () => {
    BarcodeScanner.stopScan();
    document.body.classList.remove("qrscanner");
  };

  const handleTimeEntryClick = () => {
    setIsTimeEntryModalOpen(true);
  };

  useEffect(() => {
    window.addEventListener("stop-barcode-scanning", () => {
      stopScan();
    });
  }, [searchValue]);

  useEffect(() => {
    setupAccessLevel();
  }, [
    location,
    translations,
    loggedIn,
    tabLocation,
    hasInspectionAccess,
    isOffline,
  ]);

  return (
    <>
      <IonMenu contentId="main" type="overlay" ref={menuRef}>
        <IonContent>
          <IonList id="inbox-list">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "8px",
                marginBottom: "5px",
              }}
            >
              <IonListHeader>
                <IonLabel>EAM Mobile</IonLabel>
              </IonListHeader>
              <div
                hidden={!isOffline}
                style={{ width: "100%", paddingLeft: "10px" }}
              >
                <IonItem style={{ width: "100%" }} color="danger">
                  <IonIcon
                    icon={cloudOfflineOutline}
                    style={{ marginRight: "0.5rem" }}
                    color="dark"
                  />
                  <IonText color="dark">
                    <h3 style={{ fontSize: "1rem", fontWeight: "700" }}>
                      {translatedMessages["OfflineMode"]?.MessageText ||
                        "Offline Mode"}
                    </h3>
                  </IonText>
                </IonItem>
              </div>
              <IonText
                style={{ width: "100%", paddingLeft: "10px" }}
                hidden={!isOffline}
                color="medium"
              >
                <small>
                  {
                    getBaseTechnicianPages().filter(
                      (f) => !f.unavailableInOffline
                    ).length
                  }{" "}
                  of {getBaseTechnicianPages().length} features available
                </small>
              </IonText>
            </div>
            {displayedAppPages.map((appPage: AppPage, index) => {
              const menuItem = (item: AppPage) => {
                return (
                  <IonMenuToggle key={index} autoHide={false}>
                    <IonItem
                      className={
                        location.pathname === item.url ? "selected" : ""
                      }
                      routerLink={item.url}
                      routerDirection="none"
                      lines="none"
                      detail={false}
                      button
                      disabled={item.unavailableInOffline && isOffline}
                      onClick={() => {
                        if (!item.url) {
                          startScan();
                        } else if (
                          item.title === translations["lbl_time_entry"]
                        ) {
                          handleTimeEntryClick();
                        } else if (
                          item.title === translations["lbl_mob_myschedule"]
                        ) {
                          router.push("/MySchedule");
                        }
                      }}
                    >
                      <IonIcon
                        slot="start"
                        ios={item.iosIcon}
                        md={item.mdIcon}
                      />
                      <IonLabel>{item.title}</IonLabel>
                      {item.unavailableInOffline && isOffline && (
                        <IonIcon
                          slot="end"
                          ios={cloudOfflineOutline}
                          md={cloudOfflineOutline}
                        />
                      )}
                    </IonItem>
                  </IonMenuToggle>
                );
              };
              return menuItem(appPage);
            })}
          </IonList>
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonSegment
              color="default"
              scrollable={true}
              value={tabLocation}
              onIonChange={(ev) => {
                handleSegmentChange(String(ev.detail.value));
              }}
            >
              {mainAccess && (
                <IonSegmentButton value={technician}>
                  <IonIcon icon={construct} title="Technician"></IonIcon>
                </IonSegmentButton>
              )}
              {storesAccess && (
                <IonSegmentButton value={stores}>
                  <IonIcon icon={cart} title="Stores"></IonIcon>
                </IonSegmentButton>
              )}
              {approvalAccess && (
                <IonSegmentButton value={mobileApproval}>
                  <IonIcon icon={thumbsUp} title="Mobile Approval"></IonIcon>
                </IonSegmentButton>
              )}
              {mobReqApprovalAccess && (
                <IonSegmentButton value={mobileApproval}>
                  <IonIcon icon={thumbsUp} title="Mobile Approval"></IonIcon>
                </IonSegmentButton>
              )}
              <IonSegmentButton value={settingsTab}>
                <IonIcon icon={settings} title="Settings"></IonIcon>
              </IonSegmentButton>
              {isPWA && (b2CSingleSignOutEnabled || cloudSuiteManaged) ? (
                <IonSegmentButton value={endSession}>
                  <IonIcon icon={logOut} title="Exit"></IonIcon>
                </IonSegmentButton>
              ) : (
                <IonSegmentButton value={signoff}>
                  <IonIcon icon={logOut} title="Sign Off"></IonIcon>
                </IonSegmentButton>
              )}
            </IonSegment>
          </IonToolbar>
        </IonFooter>
      </IonMenu>
    </>
  );
};

export default Menu;
